import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react'
import { TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%'
  },
  listbox: {
    maxHeight: 250,
    overflow: 'auto'
  },
  noOptions: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  input: {
    padding: theme.spacing(1),
  },
  inputRoot: {
    padding: theme.spacing(1),
  },
}))

/**
 * Predefined datasets for HardCodedDropdown
 * Each dataset is an array of objects with VALUE and DISPLAY fields
 */
const DATASETS = {
  PERIOD: [
    { VALUE: '1', DISPLAY: '1' },
    { VALUE: '2', DISPLAY: '2' },
    { VALUE: '3', DISPLAY: '3' },
    { VALUE: '4', DISPLAY: '4' },
    { VALUE: '5', DISPLAY: '5' },
    { VALUE: '6', DISPLAY: '6' },
    { VALUE: '7', DISPLAY: '7' },
    { VALUE: '8', DISPLAY: '8' },
    { VALUE: '9', DISPLAY: '9' },
    { VALUE: '10', DISPLAY: '10' },
    { VALUE: '11', DISPLAY: '11' },
    { VALUE: '12', DISPLAY: '12' },
    { VALUE: '13', DISPLAY: '13' },
    { VALUE: '99', DISPLAY: '99' },
  ],
  TRIAL_BALANCE_TYPE: [
    { VALUE: 'Amount', DISPLAY: 'Amount' },
    { VALUE: 'Unit', DISPLAY: 'Unit' },
    { VALUE: 'Budget', DISPLAY: 'Budget' },
    { VALUE: 'Currency', DISPLAY: 'Currency' },
    { VALUE: 'Budget Unit', DISPLAY: 'Budget Unit' },
    { VALUE: 'Transfer', DISPLAY: 'Transfer' },
    { VALUE: 'Currency One', DISPLAY: 'Currency One' },
    { VALUE: 'Currency Two', DISPLAY: 'Currency Two' }
  ],
  // Add more datasets here as needed
  // EXAMPLE: {
  //   VALUE: 'value1',
  //   DISPLAY: 'Display Text 1'
  // }
}

/**
 * Helper function to get error message
 * @param {boolean} touched - Whether field has been touched
 * @param {string} customError - Custom error message
 * @param {boolean} required - Whether field is required
 * @param {Object|null} selectedValue - Currently selected value
 * @param {string} label - Field label
 * @returns {string|null} Error message or null
 */
const getErrorMessage = (touched, customError, required, selectedValue, label) => {
  if (!touched) return null
  if (customError) return customError
  if (required && !selectedValue) return `${label} must be selected`
  return null
}

/**
 * HardCodedDropdown - A reusable dropdown component for predefined static datasets
 * Similar UI to OpenSearchDropdown but uses hardcoded data instead of API calls
 * 
 * @param {Object} props
 * @param {string} props.id - Unique identifier for the dropdown
 * @param {string|Array} props.dataset - Dataset name (e.g., 'PERIOD') or inline options array
 * @param {string} props.displayField - Field to display in dropdown (defaults to "DISPLAY")
 * @param {string} props.valueField - Field to use as value (defaults to "VALUE")
 * @param {string} props.label - Label for the input
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.required - Whether the input is required
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {string} props.width - Width of the dropdown
 * @param {string} props.variant - TextField variant (standard, filled, outlined)
 * @param {Function} props.onChange - Callback when value changes (receives option object or null)
 * @param {string} props.customError - Custom error message
 * @param {boolean} props.clearTrigger - Toggle this to clear the dropdown
 * @param {string} props.defaultValue - Default value to pre-select
 */
function HardCodedDropdown({
  id,
  dataset,
  label = 'Select',
  placeholder = 'Select an option...',
  required = false,
  disabled = false,
  width = '100%',
  variant = 'standard',
  displayField = 'DISPLAY',
  valueField = 'VALUE',
  onChange,
  customError = null,
  clearTrigger = false,
  defaultValue = null
}) {
  const classes = useStyles()
  const inputRef = useRef(null)
  const prevClearTriggerRef = useRef(clearTrigger)
  const onChangeRef = useRef(onChange)

  // Keep onChange ref updated without triggering effects
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // Get options from dataset. Supports named datasets and inline arrays.
  const options = useMemo(() => {
    if (Array.isArray(dataset)) {
      return dataset.map(option => {
        if (typeof option === 'string' || typeof option === 'number') {
          const value = option.toString()
          return { VALUE: value, DISPLAY: value }
        }
        return option
      })
    }

    if (!dataset || !DATASETS[dataset]) {
      console.warn(`HardCodedDropdown: Dataset "${dataset}" not found. Available datasets: ${Object.keys(DATASETS).join(', ')}`)
      return []
    }
    return DATASETS[dataset]
  }, [dataset])

  const [selectedValue, setSelectedValue] = useState(null)
  const [touched, setTouched] = useState(false)
  const initializedRef = useRef(false)

  // Helper function to find option by value
  const findOptionByValue = useCallback((value) => {
    if (!value || !options.length) return null
    return options.find(option => option[valueField] === value) || null
  }, [options, valueField])

  // Initialize default value when options are available
  useEffect(() => {
    if (!initializedRef.current && defaultValue && options.length > 0) {
      const defaultOption = findOptionByValue(defaultValue)
      if (defaultOption) {
        setSelectedValue(defaultOption)
        if (onChangeRef.current) {
          onChangeRef.current(defaultOption)
        }
        initializedRef.current = true
      }
    }
  }, [defaultValue, options, findOptionByValue])

  // Handle clear trigger changes
  useEffect(() => {
    if (clearTrigger !== prevClearTriggerRef.current) {
      prevClearTriggerRef.current = clearTrigger
      initializedRef.current = false
      if (defaultValue) {
        const defaultOption = findOptionByValue(defaultValue)
        setSelectedValue(defaultOption)
        if (onChangeRef.current) {
          onChangeRef.current(defaultOption)
        }
        initializedRef.current = true
      } else {
        setSelectedValue(null)
        if (onChangeRef.current) {
          onChangeRef.current(null)
        }
      }
      setTouched(false)
    }
  }, [clearTrigger, defaultValue, findOptionByValue])

  // Handle value change
  const handleChange = useCallback((event, newValue) => {
    setSelectedValue(newValue)
    setTouched(true)
    if (onChangeRef.current) {
      onChangeRef.current(newValue)
    }
  }, [])

  // Handle blur
  const handleBlur = useCallback(() => {
    setTouched(true)
  }, [])

  // Get error message
  const error = useMemo(() => 
    getErrorMessage(touched, customError, required, selectedValue, label),
    [touched, customError, required, selectedValue, label]
  )

  // Get option label
  const getOptionLabel = useCallback((option) => {
    if (!option) return ''
    return option[displayField] || option.DISPLAY || ''
  }, [displayField])

  // Check if option is selected
  const getOptionSelected = useCallback((option, value) => {
    if (!option || !value) return false
    return option[valueField] === value[valueField]
  }, [valueField])

  // Render option
  const renderOption = useCallback((option) => (
    <div>{option[displayField] || option.DISPLAY || ''}</div>
  ), [displayField])

  // Mark field as touched when form is submitted (for submit-time validation)
  useEffect(() => {
    const inputElement = inputRef.current
    if (!inputElement) return

    // Find the form element
    const form = inputElement.closest('form')
    if (!form) return

    const handleFormSubmit = (e) => {
      // If field is required and empty, mark as touched to show error
      if (required && !selectedValue) {
        setTouched(true)
      }
    }

    form.addEventListener('submit', handleFormSubmit)
    return () => {
      form.removeEventListener('submit', handleFormSubmit)
    }
  }, [required, selectedValue])

  return (
    <Autocomplete
      id={id}
      className={classes.root}
      style={{ width }}
      value={selectedValue}
      onChange={handleChange}
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      disabled={disabled}
      onBlur={handleBlur}
      classes={{
        listbox: classes.listbox,
        noOptions: classes.noOptions,
        ...(variant !== 'filled' && {
          input: classes.input,
          inputRoot: classes.inputRoot
        })
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={(ref) => {
            inputRef.current = ref
            if (params.inputRef) {
              params.inputRef(ref)
            }
          }}
          label={label}
          placeholder={placeholder}
          required={required}
          error={!!error}
          helperText={error}
          variant={variant}
        />
      )}
      renderOption={renderOption}
    />
  )
}

export default HardCodedDropdown

